import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ReactFormGenerator } from 'react-form-builder2';
import { useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { debounce, set } from 'lodash';
import { useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { useMacrosContext } from 'lib/context/MacrosContext/MacrosContext';
import { deepEqual, ia, iaRa, io, spaceToKebabCase } from 'lib/helpers/utility';

import {
  handleOnChange,
  hasFormData
} from 'components/practice/charts/ClinicalNote/lib/advancedFormsHelpers';
import { registerSubmitRef } from 'components/practice/charts/ClinicalNote/lib/registerSubmitRef';
import Accordion from 'components/practice/charts/ClinicalNote/shared/Accordion';
import Button from 'components/shared/Button/Button';
import { clinicalNote as clinicalNoteState } from 'components/state';

import CustomFormNarrative from '../components/CustomFormNarrative';
import { cloneFormSafely, useAdvancedFormData } from '../hooks/useAdvancedFormData';
import { useAdvancedFormSubmit } from '../hooks/useAdvancedFormSubmit';
import { useAdvancedFormSync } from '../hooks/useAdvancedFormSync';

import FormLoading from './FormLoading';

const equals = (a, b) => a != null && b != null && a == b;

const MemoFormGenerator = memo(ReactFormGenerator);

const AdvancedNoteComponent = ({
  noteType = null,
  formDataKey,
  advancedNoteRef,
  currAdvForm = {},
  withStyle = true,
  isMacro = false,
  customCN = null,
  isScrolling,
  setAdvancedNote,
  setCustomCN = () => {}
}) => {
  const [formUpdated, setFormUpdated] = useState(false);
  const [narrativeUpdated, setNarrativeUpdated] = useState(false);

  const sectionRef = useRef();

  const { createMacroType, activeMacro } = useMacrosContext() || {};
  const macroType = createMacroType || activeMacro?.type;

  const activeClinicalNote = useRecoilValue(clinicalNoteState);
  const clinicalNote = customCN?.clinicalNote || activeClinicalNote;

  const { id, appointmentId } = useParams();
  const queryClient = useQueryClient();

  const formId = currAdvForm.form_id || currAdvForm.id;
  const queryKey = useMemo(
    () => [
      'customFormsById',
      formId,
      currAdvForm?.created_at || currAdvForm?.updated_at,
      appointmentId,
      id,
      isMacro
    ],
    [formId, currAdvForm?.created_at, currAdvForm?.updated_at, appointmentId, id, isMacro]
  );

  const cnContext = useClinicalNoteContext();

  const {
    setPatientResponseSidebar,
    setSaveButtonText,
    updateSaveButton,
    forceSave,
    applyForceSave,
    cnDisplaySettings,
    noteInactiveState
  } = !isMacro ? cnContext : {};

  const activeViewType = cnDisplaySettings?.templateView?.type;

  const currentActiveForm = useMemo(() => {
    if (isMacro) {
      const isCustom = formDataKey === 'advancedCustomNote';

      const data = isCustom ? customCN?.[formDataKey]?.[noteType] : customCN?.[formDataKey];

      if (ia(data)) {
        const found = data?.find((item) => {
          return (
            equals(item.form_id, currAdvForm.form_id) ||
            equals(item.form_id, currAdvForm.id) ||
            equals(item.id, currAdvForm.form_id) ||
            equals(item.id, currAdvForm.id)
          );
        });

        return found;
      } else {
        return data ?? currAdvForm;
      }
    } else {
      return currAdvForm;
    }
  }, [currAdvForm, isMacro, customCN?.[formDataKey], noteType]);

  const updateForm = useCallback((data) => {
    setCurrentForm(cloneFormSafely(data));
    setOldForm(cloneFormSafely(data));
  }, []);

  const updateMacro = useCallback(
    (updatedForm) => {
      if (isMacro && updatedForm) {
        setCustomCN((prev) => {
          if (macroType === 'form_macro') {
            const isEqual = deepEqual(updatedForm, prev?.[formDataKey]);

            return isEqual ? prev : { [formDataKey]: updatedForm };
          } else {
            const isCustom = formDataKey === 'advancedCustomNote';
            const data = isCustom ? prev?.[formDataKey]?.[noteType] : prev?.[formDataKey];
            const newDataArray = [...iaRa(data)];

            const existingFormIndex = data?.findIndex((item) => {
              return (
                equals(item.form_id, currAdvForm.form_id) ||
                equals(item.form_id, currAdvForm.id) ||
                equals(item.id, currAdvForm.form_id) ||
                equals(item.id, currAdvForm.id)
              );
            });

            if (existingFormIndex > -1) {
              newDataArray[existingFormIndex] = {
                ...newDataArray[existingFormIndex],
                ...updatedForm
              };
            } else {
              newDataArray.unshift(updatedForm);
            }

            const newData = { ...(prev || {}) };

            const path = isCustom ? `${formDataKey}.${noteType}` : `${formDataKey}`;

            set(newData, path, newDataArray);

            return newData;
          }
        });
      }
    },
    [isMacro, formDataKey, macroType, noteType, currAdvForm]
  );

  const { currentForm, setCurrentForm, oldForm, setOldForm, isLoading, isFetching, macroVersion } =
    useAdvancedFormData({
      currAdvForm: currentActiveForm,
      queryKey,
      isMacro,
      activeViewType,
      advancedNoteRef,
      isScrolling,
      updateForm,
      updateMacro
    });

  const { isMounted, syncON, setSyncON } = useAdvancedFormSync({
    noteType,
    formDataKey,
    currentForm,
    currAdvForm: currentActiveForm,
    sectionRef,
    setPatientResponseSidebar,
    setCurrentForm,
    setNarrativeUpdated
  });

  const { handleFormSubmit } = useAdvancedFormSubmit({
    noteType,
    formDataKey,
    queryKey,
    updateForm,
    setAdvancedNote,
    setSaveButtonText,
    updateSaveButton,
    formUpdated,
    narrativeUpdated,
    setFormUpdated,
    setNarrativeUpdated,
    forceSave,
    applyForceSave,
    oldForm,
    currentForm
  });

  const narrativeUpdateHandler = useCallback(
    (value) => {
      setCurrentForm((prev) => {
        const newValue = typeof value === 'function' ? value(prev) : value;

        if (isMacro && updateMacro) updateMacro(newValue);

        return newValue;
      });
    },
    [isMacro, setCurrentForm, updateMacro]
  );

  const updateFormType = useMemo(() => {
    const parentKey = noteType;
    const childKey = `${spaceToKebabCase(currAdvForm?.form?.name || currAdvForm?.name)}-${
      currAdvForm?.form_id || currAdvForm?.id
    }`;

    return {
      parentKey,
      childKey
    };
  }, [noteType, currAdvForm]);

  const formIdentity = `${currentForm?.id ?? currentForm?.form_id}-${macroVersion}`;

  const parsedAnswerData = useMemo(() => {
    if (!currentForm?.form) return undefined;
    const fields = currentForm?.json?.fields;
    if (fields == null) return undefined;
    return typeof fields === 'string' ? JSON.parse(fields) : fields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formIdentity]);

  const formDataFields = useMemo(() => {
    return currentForm?.form ? currentForm?.form?.json?.fields : currentForm?.json?.fields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formIdentity]);

  const onChangeStateRef = useRef({});
  onChangeStateRef.current = { isMounted, currentForm, syncON };

  const debouncedHandleOnChange = useMemo(
    () =>
      debounce((args) => {
        handleOnChange(args);
      }, 50),
    []
  );

  useEffect(() => () => debouncedHandleOnChange.flush(), [debouncedHandleOnChange]);

  const onFormChange = useCallback(
    (e, formResponse) => {
      if (onChangeStateRef.current.isMounted) {
        setFormUpdated(true);
      }
      debouncedHandleOnChange({
        e,
        formResponse,
        ...onChangeStateRef.current,
        queryClient,
        queryKey,
        setCurrentForm,
        setFormUpdated,
        setNarrativeUpdated,
        updateMacro
      });
    },
    [debouncedHandleOnChange, queryClient, queryKey, updateMacro]
  );

  const submitStateRef = useRef({});
  submitStateRef.current = { isMacro, currentForm, currentActiveForm, handleFormSubmit };

  const stableOnSubmit = useCallback(
    (e) => {
      if (submitStateRef.current.isMacro) return;
      flushSync(() => {
        debouncedHandleOnChange.flush();
      });
      const {
        currentForm: cf,
        currentActiveForm: caf,
        handleFormSubmit: hfs
      } = submitStateRef.current;
      hfs(e, cf?.form ? cf?.id : null, caf.form_id || caf.id, cf?.form_version || cf?.version);
    },
    [debouncedHandleOnChange]
  );

  const currentActiveFormId = currentActiveForm?.form_id || currentActiveForm?.id;

  const stableSubmitButton = useMemo(
    () => (
      <Button
        text="Submit"
        type="submit"
        hidden
        ref={(buttonRefElement) =>
          registerSubmitRef({
            refVariable: advancedNoteRef,
            activeViewType,
            formId: currentActiveFormId,
            buttonRefElement
          })
        }
      />
    ),
    [advancedNoteRef, activeViewType, currentActiveFormId]
  );

  const readOnly = !isMacro && (clinicalNote?.locked || noteInactiveState);

  if (
    !currentForm?.json?.fields ||
    !io(currentForm) ||
    !hasFormData(currentForm) ||
    (activeViewType !== 'multiple' && (isLoading || isFetching))
  )
    return <FormLoading />;

  const macroSuffix = isMacro ? '-macro' : '';

  return (
    <div key={`${currentActiveForm?.id}${macroSuffix}`}>
      <Accordion
        withStyle={withStyle}
        title={currentActiveForm?.name ?? currentActiveForm?.form?.name}
        id={id}
        updateFormType={updateFormType}
        disabled={!isMacro && (clinicalNote?.locked || noteInactiveState)}
        sectionRef={sectionRef}>
        <div className="advanced-form h-auto whitespace-pre-wrap">
          <MemoFormGenerator
            key={`form-${currentActiveForm?.id}${macroSuffix}-${macroVersion}`}
            onChange={onFormChange}
            answer_data={parsedAnswerData}
            data={formDataFields}
            read_only={readOnly}
            submitButton={stableSubmitButton}
            onSubmit={stableOnSubmit}
          />
        </div>
        <CustomFormNarrative
          key={currentActiveForm.form_id || currentActiveForm.id}
          narrativeData={currentForm?.narrative}
          currentForm={currentForm}
          syncON={syncON}
          setNarrativeUpdated={setNarrativeUpdated}
          setCurrentForm={narrativeUpdateHandler}
          setSyncON={setSyncON}
          isMacro={isMacro}
        />
      </Accordion>
    </div>
  );
};

export default memo(AdvancedNoteComponent);
